import React from 'react';
import {
  Inbox, ScanLine, CreditCard, Handshake, Workflow, Layers,
  Rocket, Palette, ShieldCheck, TrendingUp,
} from 'lucide-react';
import { Container } from '../components/ui';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import PartnerForm from '../components/PartnerForm';
import { PARTNER_TYPES, PARTNER_BENEFITS } from '../data/content';

const ICONS = { Inbox, ScanLine, CreditCard, Handshake, Workflow, Layers, Rocket, Palette, ShieldCheck, TrendingUp };

export default function Partners() {
  return (
    <div data-testid="partners-page">
      <Seo title="Partner with BillPortal — power UBM for your clients" description="Join the BillPortal partner programme. White-label bill collection, data extraction, UBM payments, managed services, migration and platform-as-a-service for your clients." />

      {/* Hero */}
      <section className="relative overflow-hidden px-5 py-20 sm:px-8 sm:py-24">
        <div className="bp-grid-bg pointer-events-none absolute inset-0 opacity-30" />
        <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
        <Container className="relative">
          <div className="max-w-3xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Partner Programme
              </span>
              <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight text-slate-900 dark:text-white sm:text-5xl lg:text-6xl">
                Build utility bill management into your offering
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">
                Use BillPortal&rsquo;s technology and managed services to deliver bill collection, AI data extraction, payments and full UBM to your own clients — under your brand, on our infrastructure.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#apply" data-testid="partners-hero-apply" className="inline-flex h-12 items-center justify-center rounded-full bg-brand px-7 text-sm font-semibold text-white transition-colors hover:bg-brand-600">
                  Become a partner
                </a>
                <a href="#ways" className="inline-flex h-12 items-center justify-center rounded-full border border-slate-300 px-7 text-sm font-semibold text-slate-700 transition-colors hover:border-brand hover:text-brand dark:border-white/15 dark:text-slate-200">
                  Explore partnership types
                </a>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Ways to partner */}
      <section id="ways" className="border-t border-slate-100 px-5 py-16 dark:border-white/10 sm:px-8 sm:py-20">
        <Container>
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">Ways to partner with us</h2>
            <p className="mt-3 max-w-2xl text-base text-slate-600 dark:text-slate-300">Mix and match the capabilities you want to bring to your clients.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PARTNER_TYPES.map((p, i) => {
              const Icon = ICONS[p.icon] || Layers;
              return (
                <Reveal key={p.title} delay={i * 60}>
                  <div data-testid={`partner-type-${i}`} className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-lg dark:border-white/10 dark:bg-white/[0.03]">
                    <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="font-semibold text-slate-900 dark:text-white">{p.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Benefits */}
      <section className="border-t border-slate-100 bg-slate-50/60 px-5 py-16 dark:border-white/10 dark:bg-white/[0.02] sm:px-8 sm:py-20">
        <Container>
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">Why partner with BillPortal</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PARTNER_BENEFITS.map((b, i) => {
              const Icon = ICONS[b.icon] || Rocket;
              return (
                <Reveal key={b.title} delay={i * 60}>
                  <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03]">
                    <Icon className="h-6 w-6 text-brand" />
                    <h3 className="mt-4 font-semibold text-slate-900 dark:text-white">{b.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{b.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Apply */}
      <section id="apply" className="scroll-mt-20 px-5 py-16 sm:px-8 sm:py-20">
        <Container>
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Apply
              </span>
              <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight text-slate-900 dark:text-white sm:text-4xl">
                Tell us what you&rsquo;d like to build
              </h2>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600 dark:text-slate-300">
                Share a few details and the areas you&rsquo;re interested in. Our partnerships team will review your application, do a little homework, and set up an introductory call.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-slate-600 dark:text-slate-300">
                <li className="flex items-start gap-2.5"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> No commitment — this is an introduction, not a contract.</li>
                <li className="flex items-start gap-2.5"><Handshake className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> A named partnerships owner will follow up with you.</li>
                <li className="flex items-start gap-2.5"><Layers className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> We&rsquo;ll tailor the model — reseller, white-label or embedded API.</li>
              </ul>
            </Reveal>
            <Reveal delay={80}>
              <PartnerForm />
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}
